import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useT } from "@/i18n/I18nContext";

export default function Hero() {
  const { t } = useT();
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden tt-noise"
    >
      <div className="absolute inset-0">
        <img
          src="https://customer-assets.emergentagent.com/job_brand-portfolio-41/artifacts/csz0muos_Gemini_Generated_Image_jt2ae6jt2ae6jt2a.png"
          alt="Aldeia de pedra entre montanhas em Portugal"
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 tt-hero-gradient" />
      </div>

      <div className="relative z-10 mx-auto max-w-[1480px] px-6 md:px-12 pt-36 md:pt-44">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="flex items-center gap-3"
        >
          <span className="w-10 h-px bg-[var(--tt-gold)]" />
          <span className="tt-overline text-[var(--tt-gold)]">{t("hero.location")}</span>
        </motion.div>
      </div>

      <div className="relative z-10 mx-auto max-w-[1480px] px-6 md:px-12 mt-10 md:mt-14">
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="font-serif font-light text-[9vw] md:text-[6.2vw] leading-[0.95] tracking-tighter text-[var(--tt-text)] max-w-[1200px]"
        >
          {t("hero.headline_a") && <>{t("hero.headline_a")}{" "}</>}
          <em className="italic font-light text-[var(--tt-gold)]">{t("hero.headline_autenticidade")}</em>{" "}{t("hero.headline_destino")}
          <br />
          {t("hero.headline_marca")}{" "}
          <em className="italic font-semibold text-[var(--tt-text)]">{t("hero.headline_jornada")}</em>.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="mt-10 max-w-2xl text-[var(--tt-text-dim)] text-base md:text-lg leading-relaxed font-light"
        >
          {t("hero.paragraph_1")}{" "}
          <span className="text-[var(--tt-aldeia)]">Viver na Aldeia</span>{" "}{t("hero.paragraph_2")}{" "}
          <span className="text-[var(--tt-trilhos)]">Trilhos em Fúria</span>{" "}{t("hero.paragraph_3")}
          <br />
          <br />
          {t("hero.paragraph_4")}{" "}
          <em className="italic text-[var(--tt-text)]">{t("hero.paragraph_5")}</em>.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.8 }}
          className="mt-12 flex flex-col sm:flex-row gap-4"
        >
          <a href="#canais" data-testid="hero-cta-primary" className="tt-btn tt-btn-primary">
            {t("hero.cta_primary")}
          </a>
          <a href="#contacto" data-testid="hero-cta-secondary" className="tt-btn">
            {t("hero.cta_secondary")}
          </a>
        </motion.div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 z-10 border-t border-[var(--tt-border)] bg-[rgba(4,8,7,0.55)] backdrop-blur-md">
        <div className="mx-auto max-w-[1480px] px-6 md:px-12 py-5 md:py-6 grid grid-cols-2 gap-6 md:gap-12">
          <div className="flex items-center gap-4">
            <span
              className="w-2 h-2 rounded-full"
              style={{ background: "var(--tt-aldeia)", boxShadow: "0 0 18px var(--tt-aldeia)" }}
            />
            <div className="leading-tight">
              <p className="tt-overline text-[10px] text-[var(--tt-text-muted)]">{t("hero.canal_01")}</p>
              <p className="font-serif italic text-base md:text-xl text-[var(--tt-text)]">
                Viver na Aldeia
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4 md:justify-end">
            <span
              className="w-2 h-2 rounded-full"
              style={{ background: "var(--tt-trilhos)", boxShadow: "0 0 18px var(--tt-trilhos)" }}
            />
            <div className="leading-tight">
              <p className="tt-overline text-[10px] text-[var(--tt-text-muted)]">{t("hero.canal_02")}</p>
              <p className="font-serif italic text-base md:text-xl text-[var(--tt-text)]">
                Trilhos em Fúria
              </p>
            </div>
          </div>
        </div>
      </div>

      <motion.a
        href="#canais"
        data-testid="hero-scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="hidden md:flex absolute right-12 bottom-28 z-10 flex-col items-center gap-3 text-[var(--tt-text-muted)] hover:text-[var(--tt-text)] transition-colors"
      >
        <span className="tt-overline rotate-90 origin-center mt-6">{t("hero.scroll")}</span>
        <ArrowDown size={16} className="animate-bounce" />
      </motion.a>
    </section>
  );
}
